import type { PersonRefDTO, ProjectRoomItemPartDTO, ProjectRoomPartDTO } from './projectRoomPartsDTO';
import type { ProjectAssetArchiveReason } from 'models/projects/projectEnums';
import type { AssessmentTaskCreatedDTO } from 'models/dto/auditCriteriaGenericDTO';

//
// Project v2 room and apply wire shapes - everything under projects/{projectId}/rooms/{kind}.
//
// Canonical source: isoplanner-api REDLAB.ISOPlanner.Core/Data/DTOs/Project/ProjectRoomDTO.cs,
// ProjectApplyDTO.cs and ProjectRiskPrecheckDTO.cs. Property names are the camelCased C#
// properties, which is what the API's serializer puts on the wire.
//
// Two wire facts shape the optionality below:
// 1. httpService's `nullsToUndefined` interceptor turns every C# `null` into `undefined`, so a C#
//    `int?` is `number | undefined` here, never `number | null`.
// 2. Every room reads through one kind-neutral shape. `itemId` is whichever record the room is over
//    (an ObjectiveId, a NormId, a RiskId, a Role Guid, a LinkId, a TaskId) and is always text on
//    the wire - ints serialize as strings, and the roles room uses the Role Guid as-is.
//    `ProjectApplyItemResultDTO.ref` echoes the same identity back. Every Guid id is a string.
// 3. What only some rooms know is a part (projectRoomPartsDTO.ts), one kind per room read and per
//    row, not a member of the row or the envelope.
//
// An apply is a synchronous POST that returns the direct outcome list. Nothing about the call is
// persisted; re-confirming an already-applied item returns already-applied.
//

export class ProjectRoomItemDTO {
  ownerId?: string;

  stateName?: string;

  tagIds: number[] = [];

  normIds: number[] = [];

  /**
   * The room's own record id, as text: an ObjectiveId in the objectives room, a Role Guid in
   * roles.
   */
  itemId: string;

  code?: string;

  /**
   * The record's own ExternalId column, for example a package's OBJ-, RSK- or DOC- code. Undefined
   * for roles, the profile and rows without one.
   */
  externalId?: string;

  name: string;

  description?: string;

  /**
   * The record's own state column, per kind; its meaning is the room's, not Project's. Undefined for
   * a kind without one (roles, the asset and outcome inquiry rows, scope, profile, outcomes).
   */
  state?: number;

  /**
   * This record's own archived state.
   *
   * A confirmed room stores no per-item decision on the wire, so this field is also how the room
   * reports that a row was set aside.
   */
  isArchived?: boolean;

  /**
   * True when this row needs an answer before the room can confirm, by the room's own server-side
   * rule. The confirm gate refuses a confirm while a saved row stays unresolved.
   */
  unresolved?: boolean;

  /** What only some rooms know about a row: one part of the kind of the row (projectRoomPartsDTO.ts). */
  part?: ProjectRoomItemPartDTO | null;

  constructor() {
    this.itemId = '';
    this.name = '';
  }
}

/** One related record referenced by a room item - a control, in the risks room's row part `controls`. */
export interface ProjectRoomRelatedDTO {
  id?: string | null;

  code?: string;

  name?: string;
}

/** Asset answers only: one comment of an inquiry's assignee on the inquiry task. */
export class ProjectRoomInquirySignalDTO {
  /** ISO 8601. */
  created = '';

  authorId?: string | null;

  text?: string | null;

  /** The comment's `commentTrailEntryId` on the task's comment trail. */
  entryId?: number | null;
}

/** Everything a room needs to render: the item set and its confirmation facts. */
export class ProjectRoomDTO {
  projectId: number;

  /** The room's key - its identity, and the {roomKey} of the route. */
  roomKey: string;

  /** The EntityTypes value this room covers. An attribute, not its identity. */
  kind: number;

  /**
   * The decision routes this room dispatches, ordinal-sorted. Empty for a room that dispatches
   * none.
   */
  actions: string[];

  /**
   * The anchor task of this room's deliverable; an inquiry task a send creates is related to it. Undefined for a
   * room without an anchor (asset-answers).
   */
  deliverableTaskId?: number;

  /**
   * What only some rooms know about the read: one part of the kind of the read (projectRoomPartsDTO.ts);
   * absent for a room that knows nothing beyond its rows.
   */
  part?: ProjectRoomPartDTO | null;

  /** `projectWriteBlocker`: noPermission | archived | trialEnded | confirmed; absent when the apply takes the caller's writes. */
  writeBlocker?: string | null;

  items: ProjectRoomItemDTO[];

  constructor() {
    this.projectId = 0;
    this.roomKey = '';
    this.kind = 0;
    this.actions = [];
    this.items = [];
  }
}

/**
 * What every reviewed decision on the wire carries: the item, and the edited description of a room that allows one.
 *
 * Kind-neutral like ProjectRoomItemDTO. Each action has its own class with exactly the members it takes, and `action`
 * tells the server which: a member of another action is a 400 that fails the whole batch, not a silent drop. Which
 * values a room accepts is the room's `actions`, never these classes. `editDescription` is not among them: it is
 * composable with `editedDescription` instead of being an action.
 */
export abstract class ProjectApplyDecisionBaseDTO {
  /**
   * The room's own item id, as text - a Role Guid in the roles room, an int-as-string elsewhere.
   */
  itemId: string;

  /** Rooms with supportsEditDescription: the new description text, composable with the action. */
  editedDescription?: string;

  constructor(itemId: string = '') {
    this.itemId = itemId;
  }
}

/**
 * The asset answers' line as the caller read it: the time of its answer. An empty line is a line read without an
 * answer. A line another person changed since is refused (PROJECT0131).
 */
export class ProjectApplyLineDTO {
  /** ISO 8601; absent for a line read without an answer. */
  expectedModifiedOn?: string;
}

/** keep: leaves the record as it is. */
export class ProjectKeepDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'keep';

  /** Asset answers: the line as read; absent sends no check. */
  line?: ProjectApplyLineDTO;
}

/** setAside: takes the record out, with the person's words or, in the asset register, why. */
export class ProjectSetAsideDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'setAside';

  /** The set-aside justification, at most 512 characters. */
  reason?: string;

  /** Asset register: why the line goes out of use; required there by the server. */
  archiveReason?: ProjectAssetArchiveReason;
}

/** A decision about one person: appoint, unappoint and reassign. */
export abstract class ProjectPersonDecisionDTO extends ProjectApplyDecisionBaseDTO {
  /** Guid, required. */
  userId = '';
}

/** Roles, appoint: appoints the person to the role. */
export class ProjectAppointDecisionDTO extends ProjectPersonDecisionDTO {
  readonly action = 'appoint';
}

/** Roles, unappoint: takes the person out of the role. */
export class ProjectUnappointDecisionDTO extends ProjectPersonDecisionDTO {
  readonly action = 'unappoint';
}

/** Inquiry progress, reassign: gives an open inquiry to the person. */
export class ProjectReassignDecisionDTO extends ProjectPersonDecisionDTO {
  readonly action = 'reassign';
}

/** Asset and outcome inquiry, send: sends the role's inquiry to one holder. */
export class ProjectSendDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'send';

  /** Guid. The holder, required when several holders can take the work; absent, the role's one holder. */
  userId?: string;
}

/** Documents, approve: approves the document as the applying user. */
export class ProjectApproveDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'approve';
}

/** Asset answers and asset register, adjust: renames the line, and moves it when a container is given. */
export class ProjectAdjustDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'adjust';

  /** The asset's name, at most 200 characters. */
  name = '';

  /** The AssetId of the container the line goes in; absent keeps the container. */
  containerId?: number;

  /** Asset answers: the line as read; absent sends no check. */
  line?: ProjectApplyLineDTO;
}

/** Asset answers, notInUse: takes the line out of use. */
export class ProjectNotInUseDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'notInUse';

  /** Why the line goes out of use. */
  archiveReason: ProjectAssetArchiveReason;

  /** The line as read; absent sends no check. */
  line?: ProjectApplyLineDTO;

  constructor(itemId: string, archiveReason: ProjectAssetArchiveReason) {
    super(itemId);
    this.archiveReason = archiveReason;
  }
}

/** Asset answers, add: creates a line on the caller's list. */
export class ProjectAddDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'add';

  /** The asset's name, at most 200 characters. */
  name = '';

  /** The AssetId of the container the line goes in; absent adds it without a container. */
  containerId?: number;

  /** The list the line goes on (task:{taskId}). */
  targetItemId?: string;
}

/** Asset answers, remove: deletes a line the caller added. Asset register, remove: deletes the line. */
export class ProjectRemoveDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'remove';
}

/** Asset answers, confirmGroup: answers keep on every line of the group without an answer. */
export class ProjectConfirmGroupDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'confirmGroup';
}

/** Asset register, restore: brings a line out of use back. */
export class ProjectRestoreDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'restore';
}

/** Asset register, merge: merges the line and the member lines into one; the server picks the line that stays. */
export class ProjectMergeDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'merge';

  /** The other lines, 1 to 9 item ids, not the item itself. */
  memberItemIds: string[] = [];

  /** The name that stays; absent keeps the name of the line that stays. */
  name?: string;

  /** The container of a member the line that stays goes in; absent keeps its own. */
  containerId?: number;

  /** The owner role of a member the line that stays takes (Role Guid); absent keeps its own. */
  ownerRoleId?: string;

  /** The proposal of the duplicate search the merge comes from (Guid); absent for a merge a person picked. */
  proposalId?: string;
}

/** Asset register, keepApart: rules the members of a proposal (item id duplicates:{proposalId}) different bedrijfsmiddelen. */
export class ProjectKeepApartDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'keepApart';
}

/** Asset register, undoKeepApart: opens a proposal (item id duplicates:{proposalId}) a keepApart closed. */
export class ProjectUndoKeepApartDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'undoKeepApart';
}

/** Asset register, unmerge ("Toch apart houden"): takes back the merge of the merged line (the item). */
export class ProjectUnmergeDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'unmerge';
}

/** Asset register, assignRole: gives the asset an owner role. */
export class ProjectAssignRoleDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'assignRole';

  /** The asset's new owner role (Role Guid). */
  ownerRoleId = '';
}

/** Outcome answers, answer: answers an outcome. */
export class ProjectAnswerDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'answer';

  /** alreadySo | notYet | unsure. */
  answer = '';
}

/** Outcome answers, clear: empties the answer of an outcome. */
export class ProjectClearDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'clear';
}

/** Outcome answers, chooseDocument: adopts a document or puts it up for discussion. */
export class ProjectChooseDocumentDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'chooseDocument';

  /** adopt | discuss. */
  choice = '';

  /** What to discuss, at most 512 characters. */
  reason?: string;
}

/** Outcome answers, clearDocument: empties the choice of a document. */
export class ProjectClearDocumentDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'clearDocument';
}

/** Outcome answers, attachEvidence: puts a file on an outcome row as its evidence. */
export class ProjectAttachEvidenceDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'attachEvidence';
}

/** Outcome answers, detachEvidence: takes a file off an outcome row. */
export class ProjectDetachEvidenceDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'detachEvidence';
}

/** Asset and outcome answers, complete: the assignee completes the inquiry. */
export class ProjectCompleteDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'complete';
}

/** External audit, quoteRequest: requests the quote through ISOPlanner (a HubSpot deal, once per room). */
export class ProjectQuoteRequestDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'quoteRequest';
}

/** External audit, quoteRequested: marks the quote as requested by hand. */
export class ProjectQuoteRequestedDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'quoteRequested';
}

/** External audit, quoteSigned: marks the quote as signed. */
export class ProjectQuoteSignedDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'quoteSigned';
}

/** External audit, arrangedElsewhere: the organisation arranges the audit without ISOPlanner. */
export class ProjectArrangedElsewhereDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'arrangedElsewhere';
}

/** External audit, dates: records Stage 1 (optional) and Stage 2, the audit date. */
export class ProjectDatesDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'dates';

  /** ISO 8601. */
  stage1?: string;

  /** ISO 8601, required. */
  stage2?: string;
}

/** External audit, skipAudit: skips the audit; a confirm in the same apply completes the room. */
export class ProjectSkipAuditDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'skipAudit';

  /** Why, at most 500 characters. Required. */
  reason = '';
}

/** External audit, undo: takes back the moment the room's read names as its undo. */
export class ProjectUndoDecisionDTO extends ProjectApplyDecisionBaseDTO {
  readonly action = 'undo';

  /** quote | signed | arrangedElsewhere | dates | skip; it must be the room part's undo. */
  moment = '';
}

/** One reviewed decision on the wire, of any action. */
export type ProjectApplyDecisionDTO =
  | ProjectKeepDecisionDTO
  | ProjectSetAsideDecisionDTO
  | ProjectAppointDecisionDTO
  | ProjectUnappointDecisionDTO
  | ProjectReassignDecisionDTO
  | ProjectSendDecisionDTO
  | ProjectApproveDecisionDTO
  | ProjectAdjustDecisionDTO
  | ProjectNotInUseDecisionDTO
  | ProjectAddDecisionDTO
  | ProjectRemoveDecisionDTO
  | ProjectConfirmGroupDecisionDTO
  | ProjectRestoreDecisionDTO
  | ProjectMergeDecisionDTO
  | ProjectKeepApartDecisionDTO
  | ProjectUndoKeepApartDecisionDTO
  | ProjectUnmergeDecisionDTO
  | ProjectAssignRoleDecisionDTO
  | ProjectAnswerDecisionDTO
  | ProjectClearDecisionDTO
  | ProjectChooseDocumentDecisionDTO
  | ProjectClearDocumentDecisionDTO
  | ProjectAttachEvidenceDecisionDTO
  | ProjectDetachEvidenceDecisionDTO
  | ProjectCompleteDecisionDTO
  | ProjectQuoteRequestDecisionDTO
  | ProjectQuoteRequestedDecisionDTO
  | ProjectQuoteSignedDecisionDTO
  | ProjectArrangedElsewhereDecisionDTO
  | ProjectDatesDecisionDTO
  | ProjectSkipAuditDecisionDTO
  | ProjectUndoDecisionDTO;

/**
 * The apply body: the decisions and whether to complete the deliverable. No idempotency key, no
 * version token. An apply either writes what it can and reports the rest, or writes nothing at all
 * - there is no partial concept for the client to guard.
 */
export class ProjectApplyRequestDTO {
  decisions: ProjectApplyDecisionDTO[];

  /**
   * False saves the batch and leaves the deliverable open. True saves it and, when nothing in the
   * batch failed, completes the deliverable (the anchor task is completed; its milestone stays open
   * until a manager switches it). A confirm may carry an empty batch.
   */
  complete: boolean;

  /** The caller's ProgressHub connection id: the documents room reports the publication of each approved document to it. */
  connectionId?: string;

  constructor() {
    this.decisions = [];
    this.complete = false;
  }
}

/** The last change of an asset line another person made after the caller read it (PROJECT0131). */
export class ProjectApplyLineChangeDTO {
  name?: string | null;

  /** ISO 8601: when the line's answer was given (its part's answer.on now). */
  modifiedOn?: string | null;

  changedBy?: PersonRefDTO | null;
}

/** What happened to one route of one item. */
export class ProjectApplyItemResultDTO {
  /**
   * The item reference: the room's item id as text (the same string an item's `itemId`
   * carries).
   */
  ref: string;

  /** A projectRoomDecision value, including the composable editDescription. */
  action: string;

  /** applied | already-applied | failed. */
  outcome: string;

  /** The ControllerException code of a failed row, for example `PROJECT0024`. */
  code?: string;

  /** The inquiry task a send created or found. */
  createdTaskId?: number;

  /** The set-aside justification echoed back from the request, when the row was one. */
  reason?: string;

  /** Asset answers, add: the item id of a line of the same name in the same container on the list (a hint). */
  sameNameItemId?: string | null;

  /** Asset answers, adjust and notInUse refused with PROJECT0131: the line as it is now and who changed it last. */
  changed?: ProjectApplyLineChangeDTO | null;

  /** Asset register, refused with PROJECT0049: why (notInUse, keptApart, nested, stale, severalLinked, merged). */
  refusalReason?: string | null;

  /** Documents, approve: what went wrong after the approval, which stands (publishFailed). */
  warning?: string | null;

  /** Asset inquiry, complete applied: the assessment tasks created for the list's assets (PLAN-075), no starts. */
  createdAssessments?: AssessmentTaskCreatedDTO[] | null;

  constructor() {
    this.ref = '';
    this.action = '';
    this.outcome = '';
  }
}

/**
 * The direct result of one apply call. Nothing is persisted - no id, key or replay - so this is the
 * whole result, valid only as long as this response exists. The anchor task ticks server-side,
 * synchronously, when `failedCount === 0` and every item was covered (`roomConfirmed`); that tick
 * also appears through the project's room availability (`confirmed`), but only here does the apply
 * state why it did or did not confirm, rather than the caller re-deriving it from counts.
 */
export class ProjectApplyResultDTO {
  appliedCount: number;

  alreadyAppliedCount: number;

  failedCount: number;

  items: ProjectApplyItemResultDTO[];

  /** True exactly when this apply's write left the room's anchor task completed. */
  roomConfirmed: boolean;

  /**
   * A `ProjectJourneyAdvanceReasons` value naming why the room did not confirm, for example
   * `undecided` when items remain without a decision. Undefined when
   * `roomConfirmed` is true.
   */
  advanceReason?: string;

  /** The items the apply changed, as the room's read shows them after its writes (the answers rooms hand them over). */
  changedItems?: ProjectRoomItemDTO[];

  /** The status of each group of a list the apply touched (`projectInquiryGroupStatus`), by the group's item id (group:{taskId}:{containerId or 0}). */
  changedGroups?: Record<string, string>;

  /** When the apply wrote, on the server's clock. */
  stamp?: string;

  constructor() {
    this.appliedCount = 0;
    this.alreadyAppliedCount = 0;
    this.failedCount = 0;
    this.items = [];
    this.roomConfirmed = false;
  }
}

/**
 * What the risks room needs before a reviewer spends a decision: whether this organization has any
 * risk states configured at all, and whether the state the set-aside route writes exists.
 *
 * Deterministic - two counts and an id - and not an AI judgment.
 */
export class ProjectRiskPrecheckDTO {
  /**
   * False means the tenant's risk states cannot express a state change of any kind - a
   * starting-set problem.
   */
  riskStatesConfigured: boolean;

  /** How many risk states are configured, completed ones included. */
  riskStateCount: number;

  /**
   * The id the set-aside route would write; undefined when this organization has no archived
   * state.
   */
  archivedRiskStateId?: number;

  /**
   * The set-aside route can run here. False is exactly the condition behind the per-item
   * refusal.
   */
  archivedStateAvailable: boolean;

  constructor() {
    this.riskStatesConfigured = false;
    this.riskStateCount = 0;
    this.archivedStateAvailable = false;
  }
}
